import React, { useState, useEffect, useCallback } from "react";
import { Lock, Sparkles, Trash2, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, getClientId } from "../lib/api";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("coi_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [horses, setHorses] = useState([]);
  const [videos, setVideos] = useState([]);
  const [image, setImage] = useState("");
  const [prompt, setPrompt] = useState("cinematic slow motion, gentle camera push-in, soft golden hour light, elegant, fine film grain");
  const [duration, setDuration] = useState("5");
  const [horse, setHorse] = useState("");
  const [busy, setBusy] = useState(false);

  const hdr = { headers: { "X-Admin-Token": token } };

  const loadVideos = useCallback(async () => {
    try {
      const r = await api.get("/admin/videos", { headers: { "X-Admin-Token": token } });
      setVideos(r.data);
      setAuthed(true);
      localStorage.setItem("coi_admin_token", token);
    } catch (err) {
      if (err?.response?.status === 401) { setAuthed(false); toast.error("Falscher Admin-Token"); }
    }
  }, [token]);

  useEffect(() => {
    if (authed) api.get("/horses").then((r) => setHorses(r.data)).catch(() => {});
  }, [authed]);

  useEffect(() => {
    if (token) loadVideos();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authed) return;
    const anyProcessing = videos.some((v) => v.status === "processing");
    if (!anyProcessing) return;
    const id = setInterval(loadVideos, 10000);
    return () => clearInterval(id);
  }, [authed, videos, loadVideos]);

  const generate = async () => {
    if (!image) { toast.error("Bitte ein Quellbild wählen"); return; }
    setBusy(true);
    try {
      await api.post("/admin/videos/generate", { image_url: image, prompt, duration, horse: horse || null }, hdr);
      toast.success("Generierung gestartet (~1–2 Min)");
      loadVideos();
    } catch { toast.error("Fehler beim Start"); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/videos/${id}`, hdr); loadVideos(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  if (!authed) {
    return (
      <div className="mx-auto max-w-md px-4 py-20">
        <div className="grid-panel rounded-xl p-8">
          <div className="flex items-center gap-2 text-sonne"><Lock size={18} /><span className="instrument-label">Admin</span></div>
          <h1 className="mt-3 font-serif text-3xl">Video-Studio</h1>
          <p className="mt-2 text-sm text-wachs-dim">Bitte Admin-Token eingeben.</p>
          <input type="password" value={token} onChange={(e) => setToken(e.target.value)} data-testid="admin-token"
            className="coi-input mt-4" placeholder="Admin-Token" />
          <button onClick={loadVideos} data-testid="admin-login" className="mt-4 w-full bg-sonne text-nacht font-medium px-5 py-3 rounded-md hover:opacity-90 transition-opacity">Anmelden</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sonne"><Sparkles size={18} /><h1 className="font-serif text-3xl">Video-Studio (fal.ai)</h1></div>
        <button onClick={loadVideos} className="inline-flex items-center gap-2 text-sm text-wachs-dim hover:text-sonne"><RefreshCw size={15} /> Aktualisieren</button>
      </div>

      {/* Generator */}
      <div className="mt-6 grid-panel rounded-lg p-5">
        <p className="instrument-label text-wachs-dim">Quellbild (Pferd)</p>
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-3">
          {horses.map((h) => (
            <button key={h.id} onClick={() => { setImage(h.image); setHorse(h.name); }} data-testid={`admin-src-${h.id}`}
              className={`relative aspect-square rounded-md overflow-hidden border-2 transition-colors ${image === h.image ? "border-sonne" : "border-transparent hover:border-bronze/50"}`}>
              <img src={h.image} alt={h.name} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
        <label className="block mt-4"><span className="instrument-label text-wachs-dim">Prompt</span>
          <textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="admin-prompt" className="coi-input mt-1.5 resize-none" /></label>
        <div className="mt-4 flex items-center gap-3">
          <label className="text-sm text-wachs-dim">Dauer:
            <select value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="admin-duration" className="coi-input ml-2 inline-block w-auto">
              <option value="5">5s</option><option value="10">10s</option>
            </select>
          </label>
          <button onClick={generate} disabled={busy} data-testid="admin-generate" className="inline-flex items-center gap-2 bg-sonne text-nacht font-medium px-5 py-2.5 rounded-md hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Clip generieren
          </button>
        </div>
      </div>

      {/* List */}
      <h2 className="mt-8 font-serif text-2xl text-sonne">Clips</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {videos.map((v) => (
          <div key={v.id} data-testid={`admin-video-${v.id}`} className="grid-panel rounded-lg overflow-hidden">
            <div className="aspect-square bg-nacht flex items-center justify-center">
              {v.status === "completed" ? (
                <video src={`${API}/videos/${v.id}/file`} autoPlay muted loop playsInline className="w-full h-full object-cover" />
              ) : v.status === "failed" ? (
                <span className="text-danger text-xs px-3 text-center">Fehler: {v.error}</span>
              ) : (
                <span className="text-wachs-dim text-xs flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> generiert…</span>
              )}
            </div>
            <div className="p-3 flex items-center justify-between">
              <span className="text-xs text-wachs-dim truncate">{v.horse || "—"}</span>
              <button onClick={() => remove(v.id)} className="text-wachs-dim hover:text-danger"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
